import React from 'react';
import { API_BASE, toUrl } from '../lib/config';

type Row = {
  id: string;
  orderIndex: number;
  image?: string | null;
  title: string;
  text: string;
  link?: string | null;
  bg: 'green' | 'mint';
  isActive: boolean;
  updatedAt?: string;
};

type MediaOpt = { id?: string; name: string; url: string };

const FeatureStripYonetim: React.FC = () => {

  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [q, setQ] = React.useState('');
  const [mediaOpts, setMediaOpts] = React.useState<MediaOpt[]>([]);

  // Media helpers (keep select value as UUID if available)
  const extractUuid = (u?: string | null) => {
    if (!u) return '';
    const m = u.match(/([0-9a-fA-F-]{36})$/);
    return m ? m[1] : u;
  };
  const absoluteMediaUrl = (val?: string | null) => {
    if (!val) return undefined;
    const uuidRe = /^[0-9a-fA-F-]{36}$/;
    const raw = uuidRe.test(val) ? `/api/media/${val}` : val;
    // API'ye gönderirken host eklemeyelim; relatif ya da tam URL olduğu gibi kalsın
    return raw;
  };
  const ImgWithFallback: React.FC<{ src?: string | null; alt: string; height?: number }> = ({ src, alt, height = 70 }) => {
    const baseIn = src || '';
    const m = baseIn.match(/([0-9a-fA-F-]{36})$/);
    const raw = m ? `/api/media/${m[1]}` : baseIn;
    const pref = toUrl(raw);
    const [currentSrc, setCurrentSrc] = React.useState(pref);
    return (
      <img
        src={currentSrc}
        alt={alt}
        style={{ height, objectFit: 'contain', borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff' }}
        crossOrigin="anonymous"
        onError={(e) => {
          const el = e.currentTarget as HTMLImageElement;
          const cur = el.getAttribute('src') || '';
          const next = cur === pref ? raw : pref;
          if (next !== cur) el.src = next;
        }}
      />
    );
  };

  // Create
  const [createOpen, setCreateOpen] = React.useState(false);
  const [cOrder, setCOrder] = React.useState<number>(0);
  const [cImage, setCImage] = React.useState('');
  const [cTitle, setCTitle] = React.useState('');
  const [cText, setCText] = React.useState('');
  const [cLink, setCLink] = React.useState('');
  const [cBg, setCBg] = React.useState<'green' | 'mint'>('green');
  const [cActive, setCActive] = React.useState(true);
  const [creating, setCreating] = React.useState(false);

  // Edit
  const [editingId, setEditingId] = React.useState('');
  const [eOrder, setEOrder] = React.useState<number>(0);
  const [eImage, setEImage] = React.useState('');
  const [eTitle, setETitle] = React.useState('');
  const [eText, setEText] = React.useState('');
  const [eLink, setELink] = React.useState('');
  const [eBg, setEBg] = React.useState<'green' | 'mint'>('green');
  const [eActive, setEActive] = React.useState(true);
  const [saving, setSaving] = React.useState(false);

  // Delete
  const [delId, setDelId] = React.useState('');
  const [delLoading, setDelLoading] = React.useState(false);

  const fetchAll = React.useCallback(async () => {
    try {
      setLoading(true); setError('');
      const res = await fetch(toUrl('/api/feature-strip/admin'));
      if (!res.ok) throw new Error('Kayıtlar alınamadı');
      const data = await res.json();
      setItems(data || []);
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setLoading(false); }
  }, []);

  const fetchMedia = React.useCallback(async () => {
    try {
      const res = await fetch(toUrl('/api/media/admin'));
      if (!res.ok) return;
      const data = await res.json();
      setMediaOpts((data || []).map((m: any) => ({ id: m.id, name: m.name || m.originalName || m.id || m.url, url: m.url || `/api/media/${m.id}` })));
    } catch { /* ignore */ }
  }, []);

  React.useEffect(() => { fetchAll(); fetchMedia(); }, [fetchAll, fetchMedia]);

  const filtered = React.useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter(i => (
      String(i.orderIndex).includes(t) ||
      (i.title || '').toLowerCase().includes(t) ||
      (i.text || '').toLowerCase().includes(t) ||
      (i.link || '').toLowerCase().includes(t) ||
      (i.image || '').toLowerCase().includes(t)
    ));
  }, [items, q]);

  const resetCreate = () => { setCOrder(0); setCImage(''); setCTitle(''); setCText(''); setCLink(''); setCBg('green'); setCActive(true); };
  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cTitle.trim() || !cText.trim()) { setError('Lütfen başlık ve metin girin'); return; }
    try {
      setCreating(true); setError('');
      const body: any = { orderIndex: cOrder || 0, image: absoluteMediaUrl(cImage), title: cTitle.trim(), text: cText.trim(), link: cLink || undefined, bg: cBg, isActive: !!cActive };
      const res = await fetch(toUrl('/api/feature-strip/admin'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Oluşturma başarısız');
      resetCreate(); setCreateOpen(false); await fetchAll();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setCreating(false); }
  };

  const openEdit = (row: Row) => { setEditingId(row.id); setEOrder(row.orderIndex || 0); setEImage(extractUuid(row.image || '')); setETitle(row.title || ''); setEText(row.text || ''); setELink(row.link || ''); setEBg((row.bg as any) || 'green'); setEActive(!!row.isActive); };
  const cancelEdit = () => { setEditingId(''); setEOrder(0); setEImage(''); setETitle(''); setEText(''); setELink(''); setEBg('green'); setEActive(true); };
  const onSave = async (id: string) => {
    if (!eTitle.trim() || !eText.trim()) { setError('Lütfen başlık ve metin girin'); return; }
    try {
      setSaving(true); setError('');
      const body: any = { orderIndex: eOrder || 0, image: absoluteMediaUrl(eImage), title: eTitle.trim(), text: eText.trim(), link: eLink || null, bg: eBg, isActive: !!eActive };
      const res = await fetch(toUrl(`/api/feature-strip/admin/${id}`), { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('Güncelleme başarısız');
      cancelEdit(); await fetchAll();
    } catch (er: any) { setError(er.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setSaving(false); }
  };

  const onDelete = (id: string) => setDelId(id);
  const confirmDelete = async () => {
    if (!delId) return;
    try {
      setDelLoading(true);
      const res = await fetch(toUrl(`/api/feature-strip/admin/${delId}`), { method: 'DELETE' });
      if (!res.ok) throw new Error('Silme başarısız');
      setDelId(''); await fetchAll();
    } catch (e: any) { setError(e.message || 'Beklenmeyen bir hata oluştu'); }
    finally { setDelLoading(false); }
  };

  return (
    <div className="admin-page" style={{ padding: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ margin: 0 }}>Özellik Şeridi (Feature Strip)</h2>
        <div style={{ fontSize: 12, color: '#6b7280' }}>Toplam: {items.length}</div>
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 600 }}>Yeni Kart</div>
          <button onClick={() => { resetCreate(); setCreateOpen(s => !s); }} className="btn">{createOpen ? 'Kapat' : 'Yeni Ekle'}</button>
        </div>
        {createOpen && (
          <form onSubmit={onCreate} style={{ marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Sıra</label>
              <input className="input" type="number" value={cOrder} onChange={(e) => setCOrder(parseInt(e.target.value || '0', 10))} />
            </div>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Resim</label>
              <select className="input" value={cImage} onChange={(e) => setCImage(e.target.value)}>
                <option value="">Seçiniz</option>
                {mediaOpts.map(m => (
                  <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                ))}
              </select>
              {cImage && (<ImgWithFallback src={cImage} alt="image" />)}
            </div>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Başlık</label>
              <input className="input" value={cTitle} onChange={(e) => setCTitle(e.target.value)} required />
            </div>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Metin</label>
              <textarea className="input" rows={3} value={cText} onChange={(e) => setCText(e.target.value)} required />
            </div>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Link (URL)</label>
              <input className="input" value={cLink} onChange={(e) => setCLink(e.target.value)} placeholder="https://..." />
            </div>
            <div style={{ display: 'grid', gap: 6, minWidth: 0 }}>
              <label className="form-label">Arkaplan</label>
              <select className="input" value={cBg} onChange={(e) => setCBg((e.target.value as 'green' | 'mint'))}>
                <option value="green">green</option>
                <option value="mint">mint</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input id="cActive" type="checkbox" checked={cActive} onChange={(e) => setCActive(e.target.checked)} />
              <label htmlFor="cActive">Aktif</label>
            </div>
            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-primary" disabled={creating}>{creating ? 'Ekleniyor...' : 'Ekle'}</button>
            </div>
          </form>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: 12, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ara: başlık, metin, link" className="input" style={{ width: 320 }} />
            {q && <button className="btn" onClick={() => setQ('')}>Temizle</button>}
          </div>
          <button className="btn" onClick={fetchAll}>Yenile</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Sıra</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Resim</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Başlık</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Metin</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Link</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Arkaplan</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Aktif</th>
                <th style={{ textAlign: 'left', padding: '12px 16px' }}>Güncellenme</th>
                <th style={{ textAlign: 'right', padding: '12px 16px' }}>İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Yükleniyor...</td></tr>)}
              {!loading && filtered.length === 0 && (<tr><td colSpan={9} style={{ padding: 16, color: '#6b7280' }}>Kayıt bulunamadı.</td></tr>)}
              {filtered.map(row => (
                <tr key={row.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '12px 16px', width: 100 }}>
                    {editingId === row.id ? (
                      <input className="input" type="number" value={eOrder} onChange={(e) => setEOrder(parseInt(e.target.value || '0', 10))} />
                    ) : row.orderIndex}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'grid', gap: 6 }}>
                        <select className="input" value={eImage} onChange={(e) => setEImage(e.target.value)}>
                          <option value="">Seçiniz</option>
                          {mediaOpts.map(m => (
                            <option key={(m.id || m.url)} value={(m.id || m.url)}>{m.name}</option>
                          ))}
                        </select>
                        {eImage && (<ImgWithFallback src={eImage} alt={eTitle || row.title} />)}
                      </div>
                    ) : (
                      row.image ? (
                        <ImgWithFallback src={row.image} alt={row.title} />
                      ) : '-'
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 200 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eTitle} onChange={(e) => setETitle(e.target.value)} />
                    ) : row.title}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 260 }}>
                    {editingId === row.id ? (
                      <textarea className="input" rows={3} value={eText} onChange={(e) => setEText(e.target.value)} />
                    ) : row.text}
                  </td>
                  <td style={{ padding: '12px 16px', minWidth: 220 }}>
                    {editingId === row.id ? (
                      <input className="input" value={eLink} onChange={(e) => setELink(e.target.value)} placeholder="https://..." />
                    ) : (row.link || '-')}
                  </td>
                  <td style={{ padding: '12px 16px', width: 140 }}>
                    {/* Arkaplan editlenemez: düzenlemede de statik göster */}
                    {row.bg}
                  </td>
                  <td style={{ padding: '12px 16px', width: 140 }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input id={`act-${row.id}`} type="checkbox" checked={eActive} onChange={(e) => setEActive(e.target.checked)} />
                        <label htmlFor={`act-${row.id}`}>Aktif</label>
                      </div>
                    ) : (
                      <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, background: row.isActive ? '#ecfdf5' : '#fef2f2', color: row.isActive ? '#065f46' : '#991b1b', border: `1px solid ${row.isActive ? '#a7f3d0' : '#fecaca'}` }}>{row.isActive ? 'Aktif' : 'Pasif'}</span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', width: 180 }}>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : '-'}</td>
                  <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                    {editingId === row.id ? (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => onSave(row.id)} disabled={saving} className="btn btn-primary">{saving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                        <button onClick={cancelEdit} disabled={saving} className="btn">İptal</button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                        <button onClick={() => openEdit(row)} className="btn">Düzenle</button>
                        <button onClick={() => onDelete(row.id)} className="btn" style={{ background: '#dc2626', color: '#fff', border: '1px solid #dc2626' }}>Sil</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete confirm modal */}
      {delId && (
        <div role="dialog" aria-modal="true" aria-labelledby="delTitle" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 }} onClick={(e) => { if (e.target === e.currentTarget) setDelId(''); }} onKeyDown={(e) => { if (e.key === 'Escape') setDelId(''); }} tabIndex={-1}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(480px,92vw)', background: '#fff', borderRadius: 12, boxShadow: '0 10px 25px rgba(0,0,0,0.15)', overflow: 'hidden' }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h5 id="delTitle" style={{ margin: 0, fontSize: 18 }}>Silme Onayı</h5>
              <button type="button" aria-label="Close" onClick={() => setDelId('')} className="btn">Kapat</button>
            </div>
            <div style={{ padding: 16 }}>Bu kartı silmek istediğinize emin misiniz? Bu işlem geri alınamaz.</div>
            <div style={{ padding: 12, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" onClick={() => setDelId('')} disabled={delLoading} className="btn">İptal</button>
              <button type="button" onClick={confirmDelete} disabled={delLoading} className="btn btn-primary" style={{ background: '#dc2626' }}>{delLoading ? 'Siliniyor...' : 'Evet, Sil'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeatureStripYonetim;
